import React, { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CardProps, SectionProps, ButtonProps, LinkProps, BadgeProps, GridProps, ModalProps, CollapsibleSectionProps } from '../types';

/**
 * Card - A container component with subtle styling for grouping related content
 * Features a warm ivory background, rounded corners, warm shadow, and gentle hover effect
 * The hover effect can be disabled with the noHover prop
 */
export const Card: React.FC<CardProps> = ({ children, className = '', noHover = false, onClick }) => (
  <div onClick={onClick} className={`bg-neutral-50 rounded-lg shadow-soft border border-neutral-200 p-6 transition-all duration-300 ${noHover ? '' : 'hover:shadow-medium hover:transform hover:-translate-y-1'} ${className}`}>
    {children}
  </div>
);

/**
 * CardHeader - The top section of a Card component
 * Typically contains titles, descriptions, or other introductory content
 */
export const CardHeader: React.FC<CardProps> = ({ children, className = '' }) => (
  <div className={`mb-4 ${className}`}>
    {children}
  </div>
);

/**
 * CardBody - The main content area of a Card component
 * Contains the primary information or interactive elements
 */
export const CardBody: React.FC<CardProps> = ({ children, className = '' }) => (
  <div className={`${className}`}>
    {children}
  </div>
);

/**
 * CardFooter - The bottom section of a Card component
 * Often contains actions, metadata, or supplementary information
 * Includes a subtle warm border to separate it from the card body
 */
export const CardFooter: React.FC<CardProps> = ({ children, className = '' }) => (
  <div className={`mt-4 pt-4 border-t border-neutral-300 ${className}`}>
    {children}
  </div>
);


/**
 * Section - A layout component for organizing content into distinct areas
 * Features an optional header with title, subtitle, and action elements
 * Commonly used to divide pages into logical segments
 */
export const Section: React.FC<SectionProps> = ({
  children,
  title,
  subtitle,
  action,
  className = '',
  titleClassName = ''
}) => (
  <div className={`mb-8 ${className}`}>
    {(title || action) && (
      <div className="flex justify-between items-center mb-4">
        <div>
          {title && <h2 className={`text-xl font-semibold text-neutral-900 ${titleClassName}`}>{title}</h2>}
          {subtitle && <p className="text-sm text-neutral-500 mt-1">{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </div>
);

// Button components

/**
 * Button - A versatile interactive element for triggering actions
 * Supports multiple variants (primary, secondary, outline, text)
 * Includes different sizes, disabled state, and full-width option
 * Implements consistent focus and hover states for accessibility
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  fullWidth = false,
  type = 'button'
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-soft hover:transform hover:-translate-y-0.5';

  const variantClasses = {
    primary: 'bg-primary-400 hover:bg-primary-500 text-white focus:ring-primary-400 font-semibold',
    secondary: 'bg-neutral-600 hover:bg-neutral-700 text-white focus:ring-neutral-400',
    outline: 'border border-primary-400 bg-neutral-50 hover:bg-primary-50 text-neutral-900 focus:ring-primary-400',
    text: 'text-secondary-500 hover:text-neutral-900 hover:bg-primary-50 focus:ring-secondary-500'
  };

  const sizeClasses = {
    sm: 'text-xs px-3 py-1.5',
    md: 'text-sm px-4 py-2',
    lg: 'text-base px-5 py-2.5'
  };

  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${widthClass} ${className} hover:shadow-medium`}
    >
      {children}
    </button>
  );
};

// Link components

/**
 * Link - A navigation component that wraps React Router's Link
 * Provides consistent styling with multiple variants
 * Used for internal navigation within the application
 */
export const Link: React.FC<LinkProps> = ({
  children,
  to,
  variant = 'primary',
  className = ''
}) => {
  const variantClasses = {
    primary: 'text-secondary-500 hover:text-neutral-900 hover:underline transition-colors duration-200',
    secondary: 'text-neutral-900 hover:text-primary-400 hover:underline transition-colors duration-200',
    text: 'text-neutral-500 hover:text-neutral-800 hover:underline transition-colors duration-200'
  };

  return (
    <RouterLink to={to} className={`${variantClasses[variant]} ${className}`}>
      {children}
    </RouterLink>
  );
};

// Badge component

/**
 * Badge - A small visual indicator for status, categories, or counts
 * Supports multiple semantic variants with appropriate colors
 * Commonly used to highlight status, tags, or notification counts
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  className = ''
}) => {
  const variantClasses = {
    default: 'bg-neutral-100 text-neutral-600 border border-neutral-300',
    success: 'bg-success-50 text-success-700 border border-success-200',
    warning: 'bg-warning-50 text-warning-700 border border-warning-200',
    error: 'bg-error-50 text-error-700 border border-error-200',
    info: 'bg-secondary-50 text-secondary-700 border border-secondary-200'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variantClasses[variant]} ${className}`}>
      {children}
    </span>
  );
};

// Grid layout components

/**
 * Grid - A responsive layout component for arranging items in columns
 * Automatically adjusts column count based on screen size
 * Configurable column count and spacing between items
 * Useful for card layouts, galleries, and dashboard widgets
 */
export const Grid: React.FC<GridProps> = ({
  children,
  cols = 3,
  gap = 'md',
  className = ''
}) => {
  const colsClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  const gapClasses = {
    sm: 'gap-3',
    md: 'gap-6',
    lg: 'gap-8'
  };

  return (
    <div className={`grid ${colsClasses[cols]} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

/**
 * StatCard - A specialized card for displaying key metrics or statistics
 * Features a title, prominent value display, optional icon, and trend indicator
 * Commonly used in dashboards to highlight important numbers and their changes
 */
export const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon?: ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  className?: string;
}> = ({ title, value, icon, trend, className = '' }) => (
  <Card className={`${className} hover:scale-105`} noHover={false}>
    <div className="flex justify-between items-start">
      <div>
        <p className="text-sm font-medium text-neutral-500">{title}</p>
        <p className="mt-1 text-2xl font-bold text-neutral-900">{value}</p>
        {trend && (
          <div className="mt-1 flex items-center">
            <span className={`text-xs font-medium ${trend.isPositive ? 'text-success-500' : 'text-error-500'}`}>
              {trend.isPositive ? '↑' : '↓'} {Math.abs(trend.value)}%
            </span>
            <span className="text-xs text-neutral-500 ml-1">from last month</span>
          </div>
        )}
      </div>
      {icon && <div className="ml-4">{icon}</div>}
    </div>
  </Card>
);

/**
 * CollapsibleSection - A reusable component for creating collapsible content sections
 * Features smooth expand/collapse animations and customizable styling
 */
export const CollapsibleSection: React.FC<CollapsibleSectionProps & { isExpanded: boolean; onToggle: () => void; className?: string }> = ({
  title,
  children,
  isExpanded,
  onToggle,
  className = '',
  smallTitle
}) => {
  return (
    <div className={`mb-8 ${className}`}>
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full p-4 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
      >
        <h3 className={smallTitle ? "text-md font-medium text-gray-800" : "text-lg font-medium text-gray-800"}>{title}</h3>
        <svg
          className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${isExpanded ? 'transform rotate-180' : ''
            }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
      >
        <div className="pt-4">
          {children}
        </div>
      </div>
    </div>
  );
}; 